import React from 'react'
import {
  Button as MantineButton,
  Loader,
  type ButtonProps as MantineButtonProps,
} from '@mantine/core'
import styled, { css, type CSSProp } from 'styled-components'
import type {
  ButtonProps,
  StyledButtonProps,
  ButtonVariant,
} from './button.types'
import { styledVariant } from '../../util'
import { radiusScale } from '../../theme/tokens'
import type { Theme } from '../../theme'
import { Icon } from '../icon'

// `$loading` is kept in the signature for SplitButton, which reuses these.
// Loading keeps the variant's default surface (Figma Status=Loading); only the
// plain disabled state is greyed.
export const colors: Record<
  ButtonVariant,
  (obj: { theme: Theme; $loading: boolean }) => CSSProp
> = {
  primary: ({ theme }) => css`
    background-color: ${theme.semantic.interactiveDefault};
    color: ${theme.semantic.contentOnBrand};
    &:hover:not(:disabled) {
      background-color: ${theme.semantic.interactiveHover};
    }
  `,
  // Figma interactive/secondary (#f5f5f6) and secondary-hover (#e9e8eb) have no
  // exact semantic token: backgroundTertiary and borderDisabled are the closest.
  secondary: ({ theme }) => css`
    background-color: ${theme.semantic.backgroundTertiary};
    color: ${theme.semantic.contentBrand};
    &:hover:not(:disabled) {
      background-color: ${theme.semantic.borderDisabled};
      color: ${theme.semantic.contentBrandHover};
    }
  `,
  // No Figma counterpart; kept for existing call sites. Flagged for design.
  tertiary: ({ theme }) => css`
    background-color: transparent;
    color: ${theme.semantic.contentBrand};
    &:hover:not(:disabled) {
      color: ${theme.semantic.contentBrandHover};
      text-decoration: underline;
    }
  `,
  approve: ({ theme }) => css`
    background-color: ${theme.semantic.backgroundTertiary};
    color: ${theme.semantic.contentSuccess};
    &:hover:not(:disabled) {
      background-color: ${theme.semantic.borderDisabled};
    }
  `,
  reject: ({ theme }) => css`
    background-color: ${theme.semantic.backgroundTertiary};
    color: ${theme.semantic.contentError};
    &:hover:not(:disabled) {
      background-color: ${theme.semantic.borderDisabled};
    }
  `,
}

const heights: Record<NonNullable<ButtonProps['size']>, number> = {
  mini: 24,
  small: 32,
  medium: 40,
  large: 48,
}

// `data-busy`, not `data-loading`: Mantine hides the button content and draws an
// overlay for [data-loading], which would hide our own Loader and loadingText.
// Doubled class raises specificity over Mantine's own button rules, which also
// set background, colour, height and padding.
// Mantine's Button is polymorphic, which styled-components cannot infer props
// from; narrow it to the <button> element this component always renders.
type MantineButtonElement = React.ForwardRefExoticComponent<
  MantineButtonProps &
    Omit<React.ComponentPropsWithoutRef<'button'>, keyof MantineButtonProps> &
    React.RefAttributes<HTMLButtonElement>
>

export const StyledButton = styled(
  MantineButton as unknown as MantineButtonElement
)<StyledButtonProps>`
  && {
    ${styledVariant('$variant', colors)};
    ${({ theme, $size = 'medium', $shape, $stretch }) => css`
      /* Apps such as admin reset box-sizing to content-box; the fixed height
         must include the vertical padding. */
      box-sizing: border-box;
      height: ${heights[$size]}px;
      /* Mantine's root is overflow:hidden, which lets a flex item shrink to
         zero; keep the button at its content width. */
      flex-shrink: 0;
      padding: ${theme.spacing.xxs} ${theme.spacing.s};
      border: none;
      border-radius: ${$shape === 'oval'
        ? radiusScale.full
        : theme.radii.interactive};
      /* Width is only declared for stretch: a width here would beat app rules
         that size a Button through a parent selector. */
      ${$stretch ? 'width: 100%;' : ''}
      font-family: inherit;
      font-size: ${theme.typographyTokens.bodyMd.fontSize};
      /* No typography token for 14/20 medium (500) */
      font-weight: 500;
      line-height: 20px;
      letter-spacing: 0;
      white-space: nowrap;
      transition: ${theme.transitionTime} ease-in-out;

      .mantine-Button-inner {
        gap: ${theme.spacing.xs};
      }
      .mantine-Button-section {
        margin: 0;
      }
      .mantine-Button-label {
        padding: ${theme.spacing.xxxs} 0;
      }

      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px ${theme.semantic.backgroundSecondary},
          0 0 0 3px ${theme.semantic.interactiveFocus};
      }

      &:disabled:not([data-busy]) {
        background-color: ${theme.semantic.backgroundDisabled};
        color: ${theme.semantic.contentDisabled};
        text-decoration: none;
      }
      &:disabled {
        cursor: not-allowed;
      }
    `}
  }
`

// The label carries the accessible name; icons are decoration.
const DecorativeIcon = ({
  name,
  size,
}: Pick<React.ComponentProps<typeof Icon>, 'name' | 'size'>) => (
  <span aria-hidden style={{ display: 'inline-flex' }}>
    <Icon name={name} color="inherit" size={size} />
  </span>
)

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'medium',
      shape = 'default',
      loading = false,
      loadingText,
      stretch,
      disabled,
      startIcon,
      endIcon,
      children,
      startIconSize,
      endIconSize,
      onClick,
      type = 'button',
      ...rest
    },
    ref
  ) => {
    const isInert = Boolean(disabled) || loading
    // The react-aria Button this replaced stopped the click at the button, so a Button
    // inside a clickable row or card never also triggered the parent's onClick. Keep
    // that: call sites were written against it. The default action (submit) still runs.
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      onClick?.(event)
    }

    return (
      <StyledButton
        {...rest}
        ref={ref}
        type={type}
        $variant={variant}
        $size={size}
        $shape={shape}
        $stretch={stretch}
        $loading={loading}
        disabled={isInert}
        data-busy={loading || undefined}
        aria-busy={loading || undefined}
        onClick={isInert ? undefined : handleClick}
        leftSection={
          loading ? (
            <Loader
              size={20}
              color="currentColor"
              role="progressbar"
              aria-label="Loading"
              data-testid="button-spinner"
            />
          ) : (
            startIcon && (
              <DecorativeIcon name={startIcon} size={startIconSize ?? 5} />
            )
          )
        }
        rightSection={
          !loading &&
          endIcon && <DecorativeIcon name={endIcon} size={endIconSize ?? 5} />
        }
      >
        {loading
          ? loadingText && <span data-testid="loading-text">{loadingText}</span>
          : children}
      </StyledButton>
    )
  }
)

Button.displayName = 'Button'
